import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { Box, Checkbox, Chip, Collapse } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { SearchOutlined, ExpandMore, ExpandLess } from "@mui/icons-material";
import { MapViewData, MapViewQuery, ServiceBoardState } from "../../models/models";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { getServiceBoardFilters } from "../../customerServiceBoardSlice";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { dt } from "../../../../shared/themes/redesignTokens";

// =====================================================================
//  DESIGN TOKENS
// =====================================================================

// Priority codes: 1=urgent, 2=high, 4=low
const priorityColors: Record<number, string> = {
  1: "#C0392B", // Urgent
  2: "#E67E22", // High
  4: "#27AE60", // Low
};

type DatePreset = "all" | "today" | "week" | "month";

const MAX_BRANDS_VISIBLE = 8;

// =====================================================================
//  SCROLLBAR STYLES (thin, subtle)
// =====================================================================

const scrollbarSx = {
  "&::-webkit-scrollbar": {
    width: "5px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#D1D5DB",
    borderRadius: "10px",
    "&:hover": {
      background: "#9CA3AF",
    },
  },
  scrollbarWidth: "thin" as const,
  scrollbarColor: "#D1D5DB transparent",
};

// =====================================================================
//  PROPS
// =====================================================================

interface MapFilterPanelProps {
  mapData: MapViewData | undefined;
  onFilterChange: (filters: MapViewQuery) => void;
}

// =====================================================================
//  COMPONENT
// =====================================================================

const MapFilterPanel: React.FC<MapFilterPanelProps> = ({ mapData, onFilterChange }) => {
  const { translate } = useTranslation(["serviceBoard", "common"]);
  const dispatch = useAppDispatch();

  // Read filters (machine brands) from Redux store
  const { filters: boardFilters } = useAppSelector<ServiceBoardState>((state: any) => state.customerServiceBoard);

  // Fetch filters if not loaded
  useEffect(() => {
    if (!boardFilters) {
      dispatch(getServiceBoardFilters());
    }
  }, [boardFilters, dispatch]);

  // Local filter state
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [dateFrom, setDateFrom] = useState<string | undefined>(undefined);
  const [dateTo, setDateTo] = useState<string | undefined>(undefined);
  const [datePreset, setDatePreset] = useState<DatePreset>("all");
  const [priorityTypeCodes, setPriorityTypeCodes] = useState<number[]>([]);
  const [serviceTypeCodes, setServiceTypeCodes] = useState<number[]>([]);
  const [machineBrandIds, setMachineBrandIds] = useState<string[]>([]);
  const [countryIds, setCountryIds] = useState<number[]>([]);

  // Machine brand show more/less
  const [showAllBrands, setShowAllBrands] = useState(false);

  // Collapsible section state (all expanded by default)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    search: true,
    dateRange: true,
    priority: true,
    serviceType: true,
    machineBrand: true,
    country: true,
  });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Toggle a collapsible section
  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Build the filter object and notify parent
  const emitFilters = useCallback(
    (overrides?: Partial<{
      search: string;
      dateFrom: string | undefined;
      dateTo: string | undefined;
      priorityTypeCodes: number[];
      serviceTypeCodes: number[];
      machineBrandIds: string[];
      countryIds: number[];
    }>) => {
      const s = overrides?.search ?? search;
      const df = overrides?.dateFrom !== undefined ? overrides.dateFrom : dateFrom;
      const dt2 = overrides?.dateTo !== undefined ? overrides.dateTo : dateTo;
      const ptc = overrides?.priorityTypeCodes ?? priorityTypeCodes;
      const stc = overrides?.serviceTypeCodes ?? serviceTypeCodes;
      const mbi = overrides?.machineBrandIds ?? machineBrandIds;
      const cid = overrides?.countryIds ?? countryIds;

      const filters: MapViewQuery = {};
      if (s.trim()) filters.search = s.trim();
      if (df) filters.dateFrom = df;
      if (dt2) filters.dateTo = dt2;
      if (ptc.length > 0) filters.priorityTypeCodes = ptc;
      if (stc.length > 0) filters.serviceTypeCodes = stc;
      if (mbi.length > 0) filters.machineBrandIds = mbi;
      if (cid.length > 0) filters.countryIds = cid;
      onFilterChange(filters);
    },
    [search, dateFrom, dateTo, priorityTypeCodes, serviceTypeCodes, machineBrandIds, countryIds, onFilterChange]
  );

  // Emit on mount
  useEffect(() => {
    emitFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Search handler (debounced) ---
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearch(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (value.trim().length === 0) {
        emitFilters({ search: "" });
        return;
      }
      debounceRef.current = setTimeout(() => {
        emitFilters({ search: value });
      }, 400);
    },
    [emitFilters]
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        emitFilters({ search: search });
      }
    },
    [search, emitFilters]
  );

  // --- Date handlers ---
  const handleDateFromChange = useCallback(
    (value: Dayjs | null) => {
      const iso = value?.toISOString();
      setDateFrom(iso);
      setDatePreset("all");
      emitFilters({ dateFrom: iso });
    },
    [emitFilters]
  );

  const handleDateToChange = useCallback(
    (value: Dayjs | null) => {
      const iso = value?.toISOString();
      setDateTo(iso);
      setDatePreset("all");
      emitFilters({ dateTo: iso });
    },
    [emitFilters]
  );

  const handleDatePreset = useCallback(
    (preset: DatePreset) => {
      setDatePreset(preset);
      let df: string | undefined;
      let dt2: string | undefined;
      if (preset === "today") {
        df = dayjs().startOf("day").toISOString();
        dt2 = dayjs().endOf("day").toISOString();
      } else if (preset === "week") {
        df = dayjs().startOf("week").add(1, "day").toISOString(); // Monday
        dt2 = dayjs().endOf("week").add(1, "day").toISOString(); // Sunday
      } else if (preset === "month") {
        df = dayjs().startOf("month").toISOString();
        dt2 = dayjs().endOf("month").toISOString();
      }
      setDateFrom(df);
      setDateTo(dt2);
      emitFilters({ dateFrom: df, dateTo: dt2 });
    },
    [emitFilters]
  );

  // --- Priority handler ---
  const handlePriorityToggle = useCallback(
    (code: number) => {
      const next = priorityTypeCodes.includes(code)
        ? priorityTypeCodes.filter((c) => c !== code)
        : [...priorityTypeCodes, code];
      setPriorityTypeCodes(next);
      emitFilters({ priorityTypeCodes: next });
    },
    [priorityTypeCodes, emitFilters]
  );

  // --- Service type handler ---
  const handleServiceTypeToggle = useCallback(
    (code: number) => {
      const next = serviceTypeCodes.includes(code)
        ? serviceTypeCodes.filter((c) => c !== code)
        : [...serviceTypeCodes, code];
      setServiceTypeCodes(next);
      emitFilters({ serviceTypeCodes: next });
    },
    [serviceTypeCodes, emitFilters]
  );

  // --- Machine brand handler ---
  const handleBrandToggle = useCallback(
    (brandId: string) => {
      const next = machineBrandIds.includes(brandId)
        ? machineBrandIds.filter((id) => id !== brandId)
        : [...machineBrandIds, brandId];
      setMachineBrandIds(next);
      emitFilters({ machineBrandIds: next });
    },
    [machineBrandIds, emitFilters]
  );

  // --- Country handler ---
  const handleCountryToggle = useCallback(
    (id: number) => {
      const next = countryIds.includes(id)
        ? countryIds.filter((c) => c !== id)
        : [...countryIds, id];
      setCountryIds(next);
      emitFilters({ countryIds: next });
    },
    [countryIds, emitFilters]
  );

  // --- Reset ---
  const handleReset = useCallback(() => {
    setSearch("");
    setDateFrom(undefined);
    setDateTo(undefined);
    setDatePreset("all");
    setPriorityTypeCodes([]);
    setServiceTypeCodes([]);
    setMachineBrandIds([]);
    setCountryIds([]);
    onFilterChange({});
  }, [onFilterChange]);

  // --- Computed counts from mapData ---
  // Priority codes: 1=urgent, 2=high, 4=low
  const priorityCounts = useMemo((): Record<number, number> => {
    if (!mapData?.locations) return { 1: 0, 2: 0, 4: 0 };
    let urgent = 0, high = 0, low = 0;
    for (const loc of mapData.locations) {
      urgent += loc.urgentCount;
      high += loc.highCount;
      low += loc.lowCount;
    }
    return { 1: urgent, 2: high, 4: low };
  }, [mapData]);

  const serviceTypeCounts = useMemo(() => {
    if (!mapData?.locations) return { 1: 0, 2: 0, 3: 0 };
    const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0 };
    for (const loc of mapData.locations) {
      for (const req of loc.requests) {
        if (counts[req.serviceTypeCode] !== undefined) {
          counts[req.serviceTypeCode]++;
        }
      }
    }
    return counts;
  }, [mapData]);

  const countryCounts = useMemo(() => {
    if (!mapData?.locations) return [];
    const map = new Map<number, { name: string; count: number }>();
    for (const loc of mapData.locations) {
      const existing = map.get(loc.countryId);
      if (existing) {
        existing.count += loc.totalCount;
      } else {
        map.set(loc.countryId, { name: loc.countryName, count: loc.totalCount });
      }
    }
    return Array.from(map.entries())
      .map(([id, data]) => ({ id, name: data.name, count: data.count }))
      .sort((a, b) => b.count - a.count);
  }, [mapData]);

  const totalRequests = mapData?.summary?.totalRequests ?? 0;
  const totalLocations = mapData?.summary?.totalLocations ?? 0;

  // Machine brands from the filters endpoint
  const machineBrands = boardFilters?.machineBrands ?? [];
  const visibleBrands = showAllBrands ? machineBrands : machineBrands.slice(0, MAX_BRANDS_VISIBLE);

  const hasActiveFilters =
    search.trim().length > 0 ||
    !!dateFrom ||
    !!dateTo ||
    priorityTypeCodes.length > 0 ||
    serviceTypeCodes.length > 0 ||
    machineBrandIds.length > 0 ||
    countryIds.length > 0;

  // Active filter counts per section (for badges on collapsed headers)
  const sectionActiveCount: Record<string, number> = {
    search: search.trim().length > 0 ? 1 : 0,
    dateRange: (dateFrom ? 1 : 0) + (dateTo ? 1 : 0),
    priority: priorityTypeCodes.length,
    serviceType: serviceTypeCodes.length,
    machineBrand: machineBrandIds.length,
    country: countryIds.length,
  };

  // =====================================================================
  //  RENDER HELPERS
  // =====================================================================

  const datePickerSlotProps = {
    textField: {
      size: "small" as const,
      fullWidth: true,
      sx: {
        "& .MuiOutlinedInput-root": {
          borderRadius: dt.radiusSm,
          fontFamily: dt.font,
          fontSize: "12px",
          backgroundColor: dt.inputBg,
          "& fieldset": { borderColor: "transparent" },
          "&:hover fieldset": { borderColor: dt.cardBorder },
          "&.Mui-focused fieldset": { borderColor: dt.blue, borderWidth: "1.5px" },
        },
        "& .MuiInputLabel-root": { fontFamily: dt.font, fontSize: "12px" },
      },
    },
  };

  // Priority codes: 1=urgent, 2=high, 4=low
  const priorityItems: { code: number; labelKey: string }[] = [
    { code: 1, labelKey: "urgent" },
    { code: 2, labelKey: "priorityHigh" },
    { code: 4, labelKey: "priorityLow" },
  ];

  const serviceTypeItems: { code: number; labelKey: string }[] = [
    { code: 1, labelKey: "mapFilterRepair" },
    { code: 2, labelKey: "mapFilterParts" },
    { code: 3, labelKey: "mapFilterMaintenance" },
  ];

  const datePresets: { key: DatePreset; labelKey: string }[] = [
    { key: "all", labelKey: "mapFilterAllTime" },
    { key: "today", labelKey: "mapFilterToday" },
    { key: "week", labelKey: "mapFilterThisWeek" },
    { key: "month", labelKey: "mapFilterThisMonth" },
  ];

  // =====================================================================
  //  RENDER
  // =====================================================================

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      {/* Scrollable filter content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          ...scrollbarSx,
        }}
      >
        {/* SEARCH */}
        <CollapsibleSection
          title={translate("mapFilterSearch")}
          sectionKey="search"
          expanded={expandedSections.search}
          activeCount={sectionActiveCount.search}
          onToggle={toggleSection}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              background: dt.inputBg,
              borderRadius: dt.radiusSm,
              border: `1.5px solid ${searchFocused ? dt.blue : "transparent"}`,
              padding: "0 10px",
              height: "36px",
              transition: "all 0.25s ease",
              boxShadow: searchFocused ? `0 0 0 3px ${dt.blueGlow}` : "none",
              mb: "4px",
            }}
          >
            <SearchOutlined
              sx={{
                fontSize: "15px",
                color: searchFocused ? dt.blue : dt.textMuted,
                flexShrink: 0,
                transition: "color 0.2s",
              }}
            />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              placeholder={translate("mapFilterSearch")}
              style={{
                border: "none",
                outline: "none",
                width: "100%",
                fontSize: "12px",
                fontFamily: dt.font,
                color: dt.textPrimary,
                background: "transparent",
                marginLeft: "6px",
              }}
            />
          </Box>
        </CollapsibleSection>

        <Separator />

        {/* DATE RANGE */}
        <CollapsibleSection
          title={translate("mapFilterDateRange")}
          sectionKey="dateRange"
          expanded={expandedSections.dateRange}
          activeCount={sectionActiveCount.dateRange}
          onToggle={toggleSection}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: "8px" }}>
              <DatePicker
                label={translate("from")}
                value={dateFrom ? dayjs(dateFrom) : null}
                onChange={handleDateFromChange}
                slotProps={datePickerSlotProps}
              />
              <DatePicker
                label={translate("to")}
                value={dateTo ? dayjs(dateTo) : null}
                onChange={handleDateToChange}
                slotProps={datePickerSlotProps}
              />
            </Box>
          </LocalizationProvider>

          {/* DATE PRESETS */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: "4px" }}>
            {datePresets.map((p) => (
              <Chip
                key={p.key}
                label={translate(p.labelKey)}
                size="small"
                onClick={() => handleDatePreset(p.key)}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: 500,
                  height: "26px",
                  borderRadius: dt.radiusPill,
                  backgroundColor: datePreset === p.key ? dt.blue : "transparent",
                  color: datePreset === p.key ? "#FFFFFF" : dt.textSecondary,
                  border: `1px solid ${datePreset === p.key ? dt.blue : dt.cardBorder}`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: datePreset === p.key ? dt.blueHover : dt.blueSoft,
                  },
                }}
              />
            ))}
          </Box>
        </CollapsibleSection>

        <Separator />

        {/* PRIORITY */}
        <CollapsibleSection
          title={translate("mapFilterPriority")}
          sectionKey="priority"
          expanded={expandedSections.priority}
          activeCount={sectionActiveCount.priority}
          onToggle={toggleSection}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: "6px" }}>
            {priorityItems.map((item) => (
              <CheckboxRow
                key={item.code}
                label={translate(item.labelKey)}
                count={priorityCounts[item.code] ?? 0}
                checked={priorityTypeCodes.includes(item.code)}
                color={priorityColors[item.code]}
                onChange={() => handlePriorityToggle(item.code)}
              />
            ))}
          </Box>
        </CollapsibleSection>

        <Separator />

        {/* SERVICE TYPE */}
        <CollapsibleSection
          title={translate("mapFilterServiceType")}
          sectionKey="serviceType"
          expanded={expandedSections.serviceType}
          activeCount={sectionActiveCount.serviceType}
          onToggle={toggleSection}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: "6px" }}>
            {serviceTypeItems.map((item) => (
              <CheckboxRow
                key={item.code}
                label={translate(item.labelKey)}
                count={serviceTypeCounts[item.code] ?? 0}
                checked={serviceTypeCodes.includes(item.code)}
                color={dt.blue}
                onChange={() => handleServiceTypeToggle(item.code)}
              />
            ))}
          </Box>
        </CollapsibleSection>

        {/* MACHINE BRAND -- only show if there are brands */}
        {machineBrands.length > 0 && (
          <>
            <Separator />
            <CollapsibleSection
              title={translate("mapFilterMachineBrand")}
              sectionKey="machineBrand"
              expanded={expandedSections.machineBrand}
              activeCount={sectionActiveCount.machineBrand}
              onToggle={toggleSection}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: "6px" }}>
                {visibleBrands.map((brand) => (
                  <CheckboxRow
                    key={brand.id}
                    label={brand.name}
                    count={brand.count}
                    checked={machineBrandIds.includes(brand.id)}
                    color={dt.blue}
                    onChange={() => handleBrandToggle(brand.id)}
                  />
                ))}
              </Box>
              {machineBrands.length > MAX_BRANDS_VISIBLE && (
                <Box
                  onClick={() => setShowAllBrands(!showAllBrands)}
                  sx={{
                    fontSize: "12px",
                    color: dt.blue,
                    cursor: "pointer",
                    fontWeight: 500,
                    fontFamily: dt.font,
                    mt: "4px",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {showAllBrands
                    ? translate("common:showLess")
                    : `${translate("common:showMore")} (${machineBrands.length - MAX_BRANDS_VISIBLE})`}
                </Box>
              )}
            </CollapsibleSection>
          </>
        )}

        {/* COUNTRY -- only show if there are countries */}
        {countryCounts.length > 0 && (
          <>
            <Separator />
            <CollapsibleSection
              title={translate("mapFilterCountry")}
              sectionKey="country"
              expanded={expandedSections.country}
              activeCount={sectionActiveCount.country}
              onToggle={toggleSection}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", mb: "6px" }}>
                {countryCounts.map((c) => (
                  <CheckboxRow
                    key={c.id}
                    label={c.name}
                    count={c.count}
                    checked={countryIds.includes(c.id)}
                    color={dt.blue}
                    onChange={() => handleCountryToggle(c.id)}
                  />
                ))}
              </Box>
            </CollapsibleSection>
          </>
        )}

        {/* RESET ALL */}
        {hasActiveFilters && (
          <Box sx={{ mt: "8px", mb: "8px" }}>
            <Box
              onClick={handleReset}
              sx={{
                fontSize: "12px",
                color: dt.blue,
                cursor: "pointer",
                fontWeight: 500,
                fontFamily: dt.font,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {translate("mapFilterResetAll")}
            </Box>
          </Box>
        )}
      </Box>

      {/* SUMMARY BAR (sticky bottom) */}
      <Box
        sx={{
          backgroundColor: dt.headerBg,
          borderTop: `1px solid ${dt.cardBorder}`,
          padding: "10px 4px",
          mt: "auto",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            fontSize: "12px",
            fontWeight: 500,
            fontFamily: dt.font,
            color: dt.textSecondary,
            lineHeight: 1.4,
          }}
        >
          {translate("mapFilterSummary", {
            requests: totalRequests,
            locations: totalLocations,
          })}
        </Box>
      </Box>
    </Box>
  );
};

// =====================================================================
//  HELPER COMPONENTS
// =====================================================================

interface CollapsibleSectionProps {
  title: string;
  sectionKey: string;
  expanded: boolean;
  activeCount: number;
  onToggle: (key: string) => void;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  sectionKey,
  expanded,
  activeCount,
  onToggle,
  children,
}) => (
  <Box>
    <Box
      onClick={() => onToggle(sectionKey)}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        mb: expanded ? "8px" : "0px",
        py: "2px",
        borderRadius: "4px",
        transition: "all 0.15s ease",
        "&:hover": { backgroundColor: "#F8FAFC" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <Box
          sx={{
            fontSize: "10px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1.2px",
            color: "#9CA3AF",
            fontFamily: 'var(--font-sans)',
            userSelect: "none",
          }}
        >
          {title}
        </Box>
        {/* Active filter badge (shown when collapsed with active filters) */}
        {!expanded && activeCount > 0 && (
          <Box
            sx={{
              fontSize: "9px",
              fontFamily: 'var(--font-mono)',
              fontWeight: 700,
              color: "#FFFFFF",
              backgroundColor: dt.blue,
              borderRadius: "10px",
              minWidth: "16px",
              height: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 4px",
              lineHeight: 1,
            }}
          >
            {activeCount}
          </Box>
        )}
      </Box>
      {expanded ? (
        <ExpandLess sx={{ fontSize: "16px", color: "#9CA3AF", transition: "transform 0.2s" }} />
      ) : (
        <ExpandMore sx={{ fontSize: "16px", color: "#9CA3AF", transition: "transform 0.2s" }} />
      )}
    </Box>
    <Collapse in={expanded} timeout={200}>
      {children}
    </Collapse>
  </Box>
);

const Separator: React.FC = () => (
  <Box sx={{ borderTop: "1px dashed #E5E7EB", my: "10px" }} />
);

interface CheckboxRowProps {
  label: string;
  count: number;
  checked: boolean;
  color: string;
  onChange: () => void;
}

const CheckboxRow: React.FC<CheckboxRowProps> = ({ label, count, checked, color, onChange }) => (
  <Box
    onClick={onChange}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "4px 4px 4px 0",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "background-color 0.15s",
      "&:hover": { backgroundColor: "#F8FAFC" },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
      <Checkbox
        checked={checked}
        size="small"
        sx={{
          padding: "2px",
          color: "#D1D5DB",
          "&.Mui-checked": { color },
          "& .MuiSvgIcon-root": { fontSize: "16px" },
        }}
      />
      <Box
        sx={{
          width: "7px",
          height: "7px",
          borderRadius: "50%",
          backgroundColor: color,
          flexShrink: 0,
          opacity: checked ? 1 : 0.45,
          transition: "opacity 0.2s",
        }}
      />
      <Box
        sx={{
          fontSize: "12px",
          fontFamily: 'var(--font-sans)',
          fontWeight: checked ? 600 : 400,
          color: checked ? color : "#4B5563",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
    </Box>
    <Box
      sx={{
        fontSize: "10px",
        fontFamily: 'var(--font-mono)',
        fontWeight: 600,
        color: "#9CA3AF",
        backgroundColor: "#F4F6F9",
        padding: "1px 6px",
        borderRadius: "8px",
        minWidth: "20px",
        textAlign: "center",
        lineHeight: "16px",
        flexShrink: 0,
      }}
    >
      {count}
    </Box>
  </Box>
);

export default React.memo(MapFilterPanel);
